import { Component, OnInit, ElementRef, HostListener } from '@angular/core';
import { PagesComponent } from '../../../pages/pages.component';
import { TranslateService } from '@ngx-translate/core';
import { AppConfigService } from '../../../app.config.service';
import { ResellerService } from "../../../@core/services/reseller.service";
import { Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { UserType } from 'src/app/@core/modals/user-type';
import { SessionService, TelegramService, NotificationService } from 'src/app/@core/services';
import { environment } from '../../../../environments/environment';
import * as socketCluster from 'socketcluster-client';
import { DateTimeService } from 'src/app/@utils/services';

@Component({
    selector: 'app-header',
    templateUrl: 'header.component.html',
    styleUrls: ['header.component.scss']
})

export class HeaderComponent implements OnInit {
    userDisplay: boolean = false;
    languages = [];
    beepSound: HTMLAudioElement;
    getUserName: any;
    newMessageCount: any;
    newmessages: any = [];

    // notification
    showNotificationDropdown = false;
    notifications: any[] = [];
    unreadCount = 0;
    notificationLoading = false;

    // telegram
    telegramModalVisible = false;
    telegramLinked: boolean | null = null;
    telegramStatusLoading = false;
    telegramLinkLoading = false;
    telegramDisconnectLoading = false;
    telegramLink: string | null = null;
    telegramLinkCopied = false;

    private socket: any = null;
    private messageSubscription: Subscription;

    constructor(
        private sessionService: SessionService,
        public translate: TranslateService,
        private elementRef: ElementRef,
        public app: PagesComponent,
        public config: AppConfigService,
        private router: Router,
        private resellerServrice: ResellerService,
        private telegramService: TelegramService,
        private notificationService: NotificationService,
        private dateTimeService: DateTimeService
    ) {
        this.beepSound = new Audio();
        this.beepSound.src = '../../../../assets/sounds/beep-08b.mp3';
    }

    ngOnInit() {
        const languages = this.config.getLanguages();
        this.languages = languages.map((lang) => ({ label: lang, value: lang }));

        if (this.app.user.userType !== UserType.EMPLOYEE) {
            this.fetchNotifications();
            this.checkNewMessageCount();
        }

        this.sessionService.refresh(this.app.user.userType).subscribe(
            (res) => {
                this.getUserName = res.data.username;
                if (res.data.channel && this.app.user.userType !== UserType.EMPLOYEE) {
                    this.connectSocket(res.data.channel);
                }
            },
            () => {}
        );
    }

    ngOnDestroy(): void {
        if (this.socket) { this.socket.disconnect(); }
        if (this.messageSubscription) { this.messageSubscription.unsubscribe(); }
    }

    @HostListener('document:click', ['$event'])
    clickOutsideDropdown(event: any) {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.showNotificationDropdown = false;
        }
    }

    closeDropdown() {
        this.showNotificationDropdown = false;
    }

    // ── Notifications ────────────────────────────────────────────────────────

    fetchNotifications(): void {
        this.notificationService.getNotifications().subscribe(
            (res) => {
                this.notifications = res.data || [];
                const prevUnread = this.unreadCount;
                this.unreadCount = this.notifications.filter(n => !+n.isRead).length;
                if (prevUnread > 0 && this.unreadCount > prevUnread) {
                    this.beepSound.play().catch(() => {});
                }
            },
            () => {}
        );
    }

    toggleNotificationDropdown(): void {
        this.showNotificationDropdown = !this.showNotificationDropdown;
        if (this.showNotificationDropdown) {
            this.fetchNotifications();
        }
    }

    markRead(notification: any): void {
        if (!notification.id || +notification.isRead) { return; }
        this.notificationService.markRead(notification.id).subscribe(
            () => {
                notification.isRead = true;
                this.unreadCount = Math.max(0, this.unreadCount - 1);
            },
            () => {}
        );
    }

    markAllRead(): void {
        this.notificationService.markAllRead().subscribe(
            () => {
                this.notifications.forEach(n => n.isRead = true);
                this.unreadCount = 0;
            },
            () => {}
        );
    }

    private getSocketHostname(): string {
        return environment._URL_
            .replace(/^https?:\/\//, '')
            .replace(/\/$/, '')
            .replace(/^api\./, 'ds.');
    }

    connectSocket(channel: string): void {
        if (this.socket) { this.socket.disconnect(); }

        this.socket = socketCluster.create({
            hostname: this.getSocketHostname(),
            port: 443,
            secure: true,
            path: '/',
            autoReconnectOptions: {
                initialDelay: 5000,
                randomness: 5000,
                multiplier: 1.5,
                maxDelay: 60000
            },
            query: { token: 'sRMPdJxfOBJmeXFLjJHgyQH0M' }
        });

        (async () => {
            for await (const { error } of this.socket.listener('error')) {
                if (error && error.code !== 1006 && !error.message?.includes('Socket hung up')) {
                    console.error('Socket error:', error);
                }
            }
        })();

        (async () => {
            const ch = this.socket.subscribe(channel);
            for await (const data of ch) {
                let notification: any;
                try {
                    notification = typeof data === 'string' ? JSON.parse(data) : data;
                } catch (e) {
                    continue;
                }
                if (!notification) { continue; }
                this.notifications.unshift({ ...notification, createdAt: new Date().toISOString() });
                this.unreadCount++;
                this.beepSound.play().catch(() => {});
            }
        })();
    }

    interpolateMessage(message: string, data: any): string {
        if (!message) { return ''; }
        let parsed = data;
        if (typeof data === 'string') {
            try { parsed = JSON.parse(data); } catch (e) { parsed = {}; }
        }
        if (!parsed) { return message; }
        return message.replace(/\{(\w+)\}/g, (_: string, key: string) =>
            parsed[key] !== undefined ? parsed[key] : `{${key}}`
        );
    }

    // ── Telegram ─────────────────────────────────────────────────────────────

    openTelegramModal(): void {
        this.telegramLink = null;
        this.telegramLinkCopied = false;
        this.telegramModalVisible = true;
        this.telegramStatusLoading = true;
        this.telegramService.getStatus().subscribe(
            (res) => {
                this.telegramStatusLoading = false;
                this.telegramLinked = res.data.telegramLinked;
            },
            () => { this.telegramStatusLoading = false; }
        );
    }

    linkTelegram(): void {
        this.telegramLinkLoading = true;
        this.telegramLink = null;
        this.telegramLinkCopied = false;
        this.telegramService.getLink().subscribe(
            (res) => {
                this.telegramLinkLoading = false;
                this.telegramLink = res.data.link;
            },
            () => { this.telegramLinkLoading = false; }
        );
    }

    copyTelegramLink(): void {
        if (!this.telegramLink) { return; }
        navigator.clipboard.writeText(this.telegramLink).then(() => {
            this.telegramLinkCopied = true;
            setTimeout(() => { this.telegramLinkCopied = false; }, 2000);
        });
    }

    disconnectTelegram(): void {
        this.telegramDisconnectLoading = true;
        this.telegramService.disconnect().subscribe(
            () => {
                this.telegramDisconnectLoading = false;
                this.telegramLinked = false;
            },
            () => { this.telegramDisconnectLoading = false; }
        );
    }

    onTelegramModalHide(): void {
        this.telegramLink = null;
        this.telegramLinkCopied = false;
        this.telegramLinked = null;
    }

    // ── Chat messages ─────────────────────────────────────────────────────────

    checkNewMessageCount(): void {
        this.checkNewMessage();
        this.messageSubscription = interval(5000).subscribe(() => this.checkNewMessage());
    }

    checkNewMessage(): void {
        if (this.app.user.userType !== UserType.EMPLOYEE) {
            this.resellerServrice.checkNewMessage().subscribe(
                (res) => {
                    this.newmessages = res.data;
                    this.newMessageCount = res.data.length;
                },
                () => {}
            );
        }
    }

    // ── Other ─────────────────────────────────────────────────────────────────

    changeLanguage(userLang: any) {
        this.translate.use(userLang);
        localStorage.setItem('lang', userLang);
    }
}
